// Copyright 2025, University of Maryland, All Rights Reserved
import { AlertCircle, CheckCircle2, XCircle } from "lucide-react"

const SOFT_LIMIT = 10240 // 10KB
const HARD_LIMIT = 20480 // 20KB

export function CharacterCounter({ count }: { count: number }) {
  const percentage = (count / HARD_LIMIT) * 100
  const isOverSoft = count > SOFT_LIMIT
  const isOverHard = count > HARD_LIMIT

  const getColor = () => {
    if (isOverHard) return "text-destructive"
    if (isOverSoft) return "text-yellow-600 dark:text-yellow-500"
    return "text-muted-foreground"
  }

  const getIcon = () => {
    if (isOverHard) return <XCircle className="h-3 w-3" />
    if (isOverSoft) return <AlertCircle className="h-3 w-3" />
    return <CheckCircle2 className="h-3 w-3" />
  }

  const getMessage = () => {
    if (isOverHard) {
      return `${count.toLocaleString()} / ${HARD_LIMIT.toLocaleString()} characters (exceeds maximum)`
    }
    if (isOverSoft) {
      return `${count.toLocaleString()} / ${HARD_LIMIT.toLocaleString()} characters (consider splitting)`
    }
    return `${count.toLocaleString()} / ${HARD_LIMIT.toLocaleString()} characters`
  }

  return (
    <div className="flex items-center justify-between text-xs">
      <div className={`flex items-center gap-1.5 ${getColor()}`}>
        {getIcon()}
        <span>{getMessage()}</span>
      </div>
      {isOverSoft && (
        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full transition-all ${isOverHard ? "bg-destructive" : "bg-yellow-500"}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
    </div>
  )
}
